import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { fetchAppUsers, lastSeen, removeAppUser, type AppUser } from '../../api/owner';
import { Button, EmptyState, Screen } from '../../ui';

/**
 * The people using a published app — end users, not builder members.
 * Ported from src/app/(main)/users/[appId].tsx.
 */
export default function Users() {
  const { appId = '' } = useParams();
  const [users, setUsers] = useState<AppUser[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setUsers(await fetchAppUsers(appId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Those people could not be loaded.');
    }
  }, [appId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function confirmRemove(): Promise<void> {
    const userId = pending;
    dialog.current?.close();
    setPending(null);
    if (!userId) return;
    try {
      await removeAppUser(appId, userId);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That person could not be removed.');
    }
  }

  return (
    <Screen title="People">
      {error && <p role="alert" className="type-caption field-error-text">{error}</p>}

      {users.length === 0 ? (
        <EmptyState icon="account" title="Nobody yet" body="People who sign in to your published app appear here." />
      ) : (
        <ul className="project-list">
          {users.map((u) => (
            <li key={u.userId} className="card clay-card project-row">
              <span className="project-text">
                <span className="type-heading">{u.email ?? u.userId}</span>
                <span className="type-caption project-meta">{lastSeen(u)}</span>
              </span>
              <Button
                title="Remove data"
                variant="ghost"
                onPress={() => {
                  setPending(u.userId);
                  dialog.current?.showModal();
                }}
              />
            </li>
          ))}
        </ul>
      )}

      <dialog ref={dialog} aria-label="Delete this person's data" className="confirm-dialog">
        <h2 className="type-title">Delete this person&rsquo;s data?</h2>
        <p className="type-body">Everything they saved in your app is removed. This cannot be undone.</p>
        <div className="build-actions">
          <Button title="Yes, delete it" variant="dangerSoft" onPress={() => void confirmRemove()} />
          <Button title="Cancel" variant="ghost" onPress={() => { dialog.current?.close(); setPending(null); }} />
        </div>
      </dialog>
    </Screen>
  );
}
